import React, { useMemo, useState } from 'react';
import { usePermissoes } from '../../hooks/usePermissoes';
import { useIsAuthenticated } from '../../hooks/useAuth';
import {
  CheckIcon,
  InformationCircleIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  PERMISSOES,
  PERMISSOES_GRUPOS,
  isPermissaoDisponivel,
} from '../../types/usuarios';
import { usePapeis, useCriarPapel, useAtualizarPapel, useExcluirPapel } from '../../hooks/usePapeis';
import type { Papel } from '../../api/papeis';
import { useToast } from '../ui/Toast';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import { btnPrimario, btnSecundario, inputIris } from '../ui/iris';
import { extrairMensagemErro } from '../../utils/extrairMensagemErro';
import { logError } from '../../utils/clientLog';

/**
 * Matriz permissão × papel — EDITÁVEL (RBAC puro, 2026-07-19).
 *
 * Antes isto era um infográfico read-only de constantes do front. Agora os papéis
 * vêm do banco e a matriz é a tela que responde *"quero mudar o que o Comprador
 * enxerga"*: marcar/desmarcar uma célula e salvar muda **todo mundo daquele papel**,
 * sem abrir usuário nenhum (o backend revoga os tokens dos afetados).
 *
 * Edição é acumulativa e o commit é explícito: o admin mexe em várias células/papéis
 * e salva de uma vez — evita N requests e N revogações de token no meio do raciocínio.
 */
const MatrizPapeisPermissoes: React.FC = () => {
  const toast = useToast();
  // Espelha os guards do PapelService: não-ADMIN só mexe em papel cujo conjunto
  // ATUAL ele mesmo possui, e nunca no próprio papel. Sem isto a matriz oferece a
  // edição, o operador refaz as marcações e só descobre no Salvar — depois do
  // trabalho. Aqui é só UX: quem manda continua sendo o backend.
  const { permissoes: minhasPermissoes, isAdmin } = usePermissoes();
  const { user } = useIsAuthenticated();
  const meuPapelId = user?.papelId ?? null;

  /** null = pode gerenciar; string = motivo pelo qual não pode. */
  const foraDeAlcance = (papel: Papel): string | null => {
    if (isAdmin) return null;
    if (meuPapelId != null && papel.id === meuPapelId) return 'É o seu próprio papel — peça a outro administrador.';
    const alem = papel.permissoes.filter(p => !minhasPermissoes.includes(p));
    return alem.length > 0
      ? `Concede acesso que você não possui (${alem.slice(0, 3).join(', ')}${alem.length > 3 ? '…' : ''}).`
      : null;
  };
  const confirm = useConfirmDialog();
  const { data: papeis, isLoading } = usePapeis();
  const criar = useCriarPapel();
  const atualizar = useAtualizarPapel();
  const excluir = useExcluirPapel();

  /** papelId → permissões editadas (só entra aqui papel que o admin tocou). */
  const [edicoes, setEdicoes] = useState<Record<number, string[]>>({});
  const [salvando, setSalvando] = useState(false);
  const [dialogo, setDialogo] = useState<{ modo: 'novo' | 'editar'; papel?: Papel } | null>(null);

  const grupos = useMemo(
    () =>
      PERMISSOES_GRUPOS
        .map(g => ({ ...g, chaves: g.chaves.filter(isPermissaoDisponivel) }))
        .filter(g => g.chaves.length > 0),
    []
  );

  const lista = papeis ?? [];
  const dirtyIds = Object.keys(edicoes).map(Number);
  const temAlteracao = dirtyIds.length > 0;

  /** Permissões vigentes de um papel: a edição pendente, se houver; senão o que veio do banco. */
  const permissoesDe = (papel: Papel): string[] => edicoes[papel.id] ?? papel.permissoes;

  const alternar = (papel: Papel, chave: string) => {
    const atuais = permissoesDe(papel);
    const novas = atuais.includes(chave) ? atuais.filter(p => p !== chave) : [...atuais, chave];

    setEdicoes(prev => {
      // Voltou ao estado do banco? some da lista de pendências (não salva à toa).
      const original = [...papel.permissoes].sort().join('|');
      if ([...novas].sort().join('|') === original) {
        const { [papel.id]: _descartado, ...resto } = prev;
        return resto;
      }
      return { ...prev, [papel.id]: novas };
    });
  };

  const salvarTudo = async () => {
    setSalvando(true);
    let afetados = 0;
    let salvos = 0;
    // Papel pendente que sumiu da lista (excluído aqui ou por outro admin) não pode
    // ser salvo — mas também não pode virar "Papéis salvos" em silêncio, senão a UI
    // afirma ter gravado o que descartou.
    const perdidos: number[] = [];
    try {
      for (const id of dirtyIds) {
        const papel = lista.find(p => p.id === id);
        if (!papel) {
          perdidos.push(id);
          continue;
        }
        const resp = await atualizar.mutateAsync({
          id,
          data: { nome: papel.nome, descricao: papel.descricao, permissoes: edicoes[id] },
        });
        afetados += resp.data.data?.qtdeUsuarios ?? 0;
        salvos++;
      }
      setEdicoes({});
      if (perdidos.length > 0 && salvos === 0) {
        toast.erro('Nada foi salvo: o papel que você editou não existe mais.');
      } else if (perdidos.length > 0) {
        toast.aviso(`${salvos} papel(éis) salvo(s). ${perdidos.length} foi(ram) descartado(s) — não existe(m) mais.`);
      } else {
        toast.sucesso(
          afetados > 0
            ? `Papéis salvos — ${afetados} usuário(s) já estão com o acesso novo.`
            : 'Papéis salvos.'
        );
      }
    } catch (error: unknown) {
      logError('Erro ao salvar papéis:', error);
      toast.erro(extrairMensagemErro(error, 'Erro ao salvar papéis.'));
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (papel: Papel) => {
    const ok = await confirm({
      titulo: `Excluir o papel "${papel.nome}"?`,
      mensagem:
        papel.qtdeUsuarios > 0
          ? `${papel.qtdeUsuarios} usuário(s) usam este papel. Mova-os para outro papel antes — a exclusão vai ser recusada.`
          : 'O papel será removido. Esta ação não pode ser desfeita.',
      textoConfirmar: 'Excluir',
      destrutivo: true,
    });
    if (!ok) return;
    try {
      await excluir.mutateAsync(papel.id);
      // Descarta a edição pendente deste papel — senão o rodapé segue anunciando
      // alteração não salva de um papel que não existe mais.
      setEdicoes(prev => {
        const { [papel.id]: _descartado, ...resto } = prev;
        return resto;
      });
      toast.sucesso(`Papel "${papel.nome}" excluído.`);
    } catch (error: unknown) {
      logError('Erro ao excluir papel:', error);
      toast.erro(extrairMensagemErro(error, 'Erro ao excluir papel.'));
    }
  };

  if (isLoading) {
    return <p className="px-1 py-6 text-sm text-tx-3">Carregando papéis…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2 rounded-iris border border-accent-ring bg-accent-soft px-3 py-2.5">
          <InformationCircleIcon className="h-4 w-4 shrink-0 text-accent-text mt-0.5" />
          <p className="text-[13px] text-tx">
            O papel é a <strong>única</strong> fonte de permissão. Marcar ou desmarcar aqui e salvar
            muda <strong>todos os usuários daquele papel</strong> — sem abrir usuário por usuário.
            Quem está logado passa a valer o acesso novo em até 1 minuto.
          </p>
        </div>
        <button type="button" onClick={() => setDialogo({ modo: 'novo' })} className={`${btnPrimario} h-9 shrink-0 px-3 text-sm`}>
          <PlusIcon className="h-4 w-4" /> Novo papel
        </button>
      </div>

      {lista.length === 0 ? (
        <div className="rounded-iris-lg border border-line bg-surface px-4 py-8 text-center">
          <p className="text-sm text-tx-2">Nenhum papel cadastrado.</p>
          <p className="mt-1 text-[13px] text-tx-3">Crie o primeiro para começar a dar acesso aos usuários.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-iris-lg border border-line bg-surface">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-line">
                <th className="sticky left-0 z-10 min-w-[240px] bg-surface px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-tx-3">
                  Permissão
                </th>
                {lista.map(papel => {
                  const dirty = edicoes[papel.id] !== undefined;
                  const bloqueio = foraDeAlcance(papel);
                  return (
                    <th key={papel.id} className="whitespace-nowrap px-3 py-2 text-center align-top">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={`text-[13px] font-semibold ${dirty ? 'text-accent-text' : bloqueio ? 'text-tx-3' : 'text-tx'}`}
                          title={bloqueio ? `Somente leitura — ${bloqueio}` : papel.descricao ?? undefined}
                        >
                          {papel.nome}
                          {dirty && ' •'}
                          {bloqueio && ' 🔒'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] font-normal text-tx-3">
                          <UsersIcon className="h-3 w-3" />
                          {papel.qtdeUsuarios}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => setDialogo({ modo: 'editar', papel })}
                            disabled={!!bloqueio}
                            className="rounded-iris-sm p-1 text-tx-3 hover:bg-surface-2 hover:text-tx disabled:cursor-not-allowed disabled:opacity-40"
                            title={bloqueio ? `Somente leitura — ${bloqueio}` : 'Renomear / descrição'}
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                          </button>
                          {!papel.sistema && (
                            <button
                              type="button"
                              onClick={() => handleExcluir(papel)}
                              disabled={!!bloqueio}
                              className="rounded-iris-sm p-1 text-tx-3 hover:bg-danger-soft hover:text-danger disabled:cursor-not-allowed disabled:opacity-40"
                              title={bloqueio ? `Somente leitura — ${bloqueio}` : 'Excluir papel'}
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grupos.map(grupo => (
                <React.Fragment key={grupo.label}>
                  <tr className="bg-surface-2">
                    <td colSpan={lista.length + 1} className="sticky left-0 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-tx-2">
                      {grupo.label}
                    </td>
                  </tr>
                  {grupo.chaves.map(chave => (
                    <tr key={chave} className="border-b border-line last:border-b-0 hover:bg-surface-2">
                      <td className="sticky left-0 z-10 whitespace-nowrap bg-surface px-4 py-1.5 text-[13px] text-tx">
                        {PERMISSOES[chave].label}
                      </td>
                      {lista.map(papel => {
                        const tem = permissoesDe(papel).includes(chave);
                        const bloqueio = foraDeAlcance(papel);
                        return (
                          <td key={papel.id} className="px-3 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={() => alternar(papel, chave)}
                              disabled={!!bloqueio}
                              aria-pressed={tem}
                              aria-disabled={!!bloqueio}
                              title={bloqueio ? `Somente leitura — ${bloqueio}` : undefined}
                              aria-label={`${PERMISSOES[chave].label} — ${papel.nome}`}
                              className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-iris-sm border transition-colors ${
                                tem
                                  ? 'border-accent-ring bg-accent-soft hover:opacity-80'
                                  : 'border-line bg-surface-2 hover:border-line-strong'
                              }`}
                            >
                              {tem && <CheckIcon className="h-3 w-3 text-accent-text" strokeWidth={3} />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {temAlteracao && (
        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-iris border border-accent-ring bg-accent-soft px-3 py-2.5">
          <p className="text-[13px] text-tx">
            <strong>{dirtyIds.length}</strong> papel(éis) com alterações não salvas.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setEdicoes({})} disabled={salvando} className={`${btnSecundario} h-8 px-3 text-xs`}>
              Descartar
            </button>
            <button type="button" onClick={salvarTudo} disabled={salvando} className={`${btnPrimario} h-8 px-3 text-xs`}>
              {salvando && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
              {salvando ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      )}

      {dialogo && (
        <DialogoPapel
          modo={dialogo.modo}
          papel={dialogo.papel}
          onFechar={() => setDialogo(null)}
          onSalvar={async (nome, descricao) => {
            try {
              if (dialogo.modo === 'novo') {
                await criar.mutateAsync({ nome, descricao, permissoes: [] });
                toast.sucesso(`Papel "${nome}" criado. Marque as permissões dele na matriz.`);
              } else if (dialogo.papel) {
                await atualizar.mutateAsync({
                  id: dialogo.papel.id,
                  data: { nome, descricao, permissoes: permissoesDe(dialogo.papel) },
                });
                toast.sucesso('Papel atualizado.');
              }
              setDialogo(null);
            } catch (error: unknown) {
              logError('Erro ao salvar papel:', error);
              toast.erro(extrairMensagemErro(error, 'Erro ao salvar papel.'));
            }
          }}
        />
      )}
    </div>
  );
};

/** Diálogo enxuto de nome/descrição — criar papel novo ou renomear existente. */
const DialogoPapel: React.FC<{
  modo: 'novo' | 'editar';
  papel?: Papel;
  onFechar: () => void;
  onSalvar: (nome: string, descricao: string) => Promise<void>;
}> = ({ modo, papel, onFechar, onSalvar }) => {
  const [nome, setNome] = useState(papel?.nome ?? '');
  const [descricao, setDescricao] = useState(papel?.descricao ?? '');
  const [salvando, setSalvando] = useState(false);
  const valido = nome.trim().length >= 2;

  const submeter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido || salvando) return;
    setSalvando(true);
    try {
      await onSalvar(nome.trim(), descricao.trim());
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onFechar}>
      <div className="w-full max-w-md rounded-iris-lg border border-line bg-surface p-4 shadow-iris-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-tx">{modo === 'novo' ? 'Novo papel' : 'Editar papel'}</h3>
        <form onSubmit={submeter} className="mt-3 space-y-3">
          <div>
            <label htmlFor="papel-nome" className="mb-1 block text-[13px] font-medium text-tx">Nome</label>
            <input
              id="papel-nome"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex.: Comprador, Fiscal, Estoquista"
              autoFocus
              className={`w-full ${inputIris}`}
            />
          </div>
          <div>
            <label htmlFor="papel-desc" className="mb-1 block text-[13px] font-medium text-tx">
              Descrição <span className="font-normal text-tx-3">(opcional)</span>
            </label>
            <input
              id="papel-desc"
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="O que este cargo faz no dia a dia"
              className={`w-full ${inputIris}`}
            />
          </div>
          {papel?.sistema && (
            <p className="text-xs text-tx-3">
              Papel base da instalação — pode ser editado, mas não excluído.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onFechar} className={`${btnSecundario} h-8 px-3 text-xs`}>Cancelar</button>
            <button type="submit" disabled={!valido || salvando} className={`${btnPrimario} h-8 px-3 text-xs`}>
              {salvando && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatrizPapeisPermissoes;
